
import {toRefs, reactive, defineComponent, PropType, ref, computed} from 'vue'
import {useI18n} from 'vue-i18n'
import DealDTO from "@/models/DealDTO";
import InvoiceStateDTO from "@/models/InvoiceStateDTO";
import APIClient from "@/service/APIClient";
import {
  ThreeDs2Requisites,
  isThreeDs2Requisites,
  runThreeDsMethod,
  collectBrowserPayload,
  submitChallenge,
} from "@/service/threeDsTwo";

const api = new APIClient();

export default defineComponent({
  props: {
    currentDeal: {
      type: Object as PropType<DealDTO | null>,
      default: null
    },
    invoice: {
      type: Object as PropType<InvoiceStateDTO>,
      default: false
    },
    paymentOption: {
      type: String,
      default: 'CARD_ACQUIRING'
    }
  },
  emits: ['deal:created', '3ds:submitted'],
  setup(props, { emit }) {
    const { t } = useI18n({ useScope: 'global' })
    const isLoading = ref(false);
    const submitError = ref('');
    const threeDsCode = ref('');
    const redirectProcessing = ref(false);
    const isRedirect = computed(() => props.paymentOption === 'CARD_ACQUIRING_REDIRECT');
    const showCardForm = computed(() => !props.currentDeal && !redirectProcessing.value);
    const isRedirectProcessing = computed(() => isRedirect.value && !showCardForm.value);
    
    const cardForm = reactive({
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cardholderName: '',
      cvv: ''
    });

    const errors = reactive({
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cardholderName: '',
      cvv: '',
      threeDsCode: ''
    });

    const formatCardNumber = (e: Event) => {
      const input = e.target as HTMLInputElement;
      let value = input.value.replace(/\s/g, '');
      if (!/^\d*$/.test(value)) {
        value = value.replace(/\D/g, '');
      }
      if (value.length > 16) {
        value = value.slice(0, 16);
      }
      const formatted = value.replace(/(.{4})/g, '$1 ').trim();
      cardForm.cardNumber = formatted;
      errors.cardNumber = '';
    };

    const formatCardholderName = (e: Event) => {
      const input = e.target as HTMLInputElement;
      let value = input.value.toUpperCase();
      value = value.replace(/[^A-Z\s]/g, '');
      cardForm.cardholderName = value;
      errors.cardholderName = '';
    };

    const validateCardForm = (): boolean => {
      let isValid = true;
      
      const cardNumber = cardForm.cardNumber.replace(/\s/g, '');
      if (!cardNumber || cardNumber.length < 13 || cardNumber.length > 19) {
        errors.cardNumber = 'Invalid card number';
        isValid = false;
      } else {
        errors.cardNumber = '';
      }

      const month = parseInt(cardForm.expiryMonth);
      if (!cardForm.expiryMonth || month < 1 || month > 12) {
        errors.expiryMonth = 'Invalid month';
        isValid = false;
      } else {
        errors.expiryMonth = '';
      }

      const year = cardForm.expiryYear.length === 2 ? `20${cardForm.expiryYear}` : cardForm.expiryYear;
      const yearNum = parseInt(year);
      const currentYear = new Date().getFullYear();
      if (!cardForm.expiryYear || yearNum < currentYear || yearNum > currentYear + 20) {
        errors.expiryYear = 'Invalid year';
        isValid = false;
      } else {
        errors.expiryYear = '';
      }

      if (!cardForm.cardholderName || cardForm.cardholderName.length < 2) {
        errors.cardholderName = 'Invalid cardholder name';
        isValid = false;
      } else {
        errors.cardholderName = '';
      }

      if (!cardForm.cvv || cardForm.cvv.length < 3 || cardForm.cvv.length > 4 || !/^\d+$/.test(cardForm.cvv)) {
        errors.cvv = 'Invalid CVV';
        isValid = false;
      } else {
        errors.cvv = '';
      }

      return isValid;
    };

    const validate3dsCode = (): boolean => {
      if (!threeDsCode.value || threeDsCode.value.length < 4 || threeDsCode.value.length > 10) {
        errors.threeDsCode = 'Invalid 3DS code';
        return false;
      }
      errors.threeDsCode = '';
      return true;
    };

    const runThreeDs2Flow = async (requisites: ThreeDs2Requisites) => {
      await runThreeDsMethod(requisites.threeDsMethodUrl, requisites.threeDsMethodData);

      let proceed;
      try {
        proceed = await api.proceed3ds2(props.invoice.id, collectBrowserPayload());
      } catch (error: any) {
        submitError.value = error.response?.data?.message
          || (t('invoice.acquiring.redirect-error') as string);
        return;
      }

      if (!proceed?.acsUrl || !proceed?.cReq) {
        submitError.value = t('invoice.acquiring.redirect-error') as string;
        return;
      }

      submitChallenge(proceed.acsUrl, proceed.cReq);
    };

    const handleCardSubmit = async () => {
      submitError.value = '';
      
      if (!validateCardForm()) {
        return;
      }

      isLoading.value = true;

      try {
        const cardNumber = cardForm.cardNumber.replace(/\s/g, '');
        const expiryYear = cardForm.expiryYear.length === 2 ? cardForm.expiryYear : cardForm.expiryYear.slice(-2);
        
        const cardData = {
          cardNumber: cardNumber,
          expiryMonth: cardForm.expiryMonth.padStart(2, '0'),
          expiryYear: expiryYear,
          cardholderName: cardForm.cardholderName.trim(),
          cvv: cardForm.cvv
        };

        const userCardBase64 = btoa(JSON.stringify(cardData));
        const isRedirect = props.paymentOption === 'CARD_ACQUIRING_REDIRECT';
        const options: any = {
          paymentOption: props.paymentOption,
          userCard: userCardBase64
        };
        if (isRedirect) {
          options.crossBorderCurrency = props.invoice.crossBorderCurrency || 'USD';
          redirectProcessing.value = true;
        }

        const invoice = await api.startDeal(
          props.invoice.id,
          isRedirect ? 'multitransfer' : 'unknown',
          options
        );

        if (isRedirect) {
          const requisites = invoice.deals[0]?.requisites;

          if (isThreeDs2Requisites(requisites)) {
            await runThreeDs2Flow(requisites);
            return;
          }

          const url = requisites?.url;
          if (!url) {
            redirectProcessing.value = false;
            submitError.value = t('invoice.acquiring.redirect-error') as string;
            return;
          }
          window.location.href = url;
          return;
        }

        emit('deal:created', invoice);
      } catch (error: any) {
        if (props.paymentOption === 'CARD_ACQUIRING_REDIRECT') {
          redirectProcessing.value = false;
          submitError.value = t('invoice.acquiring.redirect-error') as string;
        } else {
          submitError.value = error.response?.data?.message || error.message || 'Failed to create deal';
        }
      } finally {
        isLoading.value = false;
      }
    };

    const handle3dsSubmit = async () => {
      submitError.value = '';
      
      if (!validate3dsCode()) {
        return;
      }

      isLoading.value = true;

      try {
        await api.sendAcquiringCode(props.invoice.id, threeDsCode.value);
        emit('3ds:submitted');
      } catch (error: any) {
        submitError.value = error.response?.data?.message || error.message || 'Failed to submit 3DS code';
      } finally {
        isLoading.value = false;
      }
    };

    return {
      ...toRefs(cardForm),
      cardForm,
      errors,
      isLoading,
      submitError,
      threeDsCode,
      redirectProcessing,
      showCardForm,
      isRedirectProcessing,
      formatCardNumber,
      formatCardholderName,
      handleCardSubmit,
      handle3dsSubmit
    }
  }
})
